import React, { useState } from "react";
import {
  Card,
  Button,
  Tag,
  Typography,
  Avatar,
  Row,
  Col,
  Divider,
  Modal,
  Input,
  Space,
  Descriptions,
  Image,
} from "antd";
import {
  ArrowLeftOutlined,
  UserOutlined,
  PhoneOutlined,
  MailOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ClockCircleOutlined,
  CalendarOutlined,
  DollarOutlined,
  CommentOutlined,
} from "@ant-design/icons";
import { useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import {
  useGetConsultantApplicationByIdQuery,
  useUpdateApplicationStatusMutation,
} from "../../../redux/rtk/apiRtk";
import { primaryColor } from "../../../utils/constants";
import { ToastContainer, toast } from "react-toastify";

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;

export const ApplicationDetail = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [actionType, setActionType] = useState("");
  const [remarks, setRemarks] = useState("");

  const {
    data: application,
    isLoading,
    refetch,
  } = useGetConsultantApplicationByIdQuery(id);

  const [updateStatus, { isLoading: isUpdating }] =
    useUpdateApplicationStatusMutation();

  if (isLoading) {
    return (
      <div className="p-6">
        <Card loading={true} />
      </div>
    );
  }

  if (!application) {
    return (
      <div className="p-6">
        <Card>
          <div className="text-center py-8">
            <Text type="secondary">Application not found</Text>
          </div>
        </Card>
      </div>
    );
  }

  const getStatusColor = (status) => {
    switch (status) {
      case "pending":
        return "orange";
      case "approved":
        return "green";
      case "rejected":
        return "red";
      default:
        return "default";
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "pending":
        return <ClockCircleOutlined />;
      case "approved":
        return <CheckCircleOutlined />;
      case "rejected":
        return <CloseCircleOutlined />;
      default:
        return <ClockCircleOutlined />;
    }
  };

  const handleStatusUpdate = async () => {
    if (!remarks.trim() && actionType === "rejected") {
      toast.error("Please provide remarks for rejection");
      return;
    }

    try {
      await updateStatus({
        id,
        status: actionType,
        remarks: remarks.trim(),
      }).unwrap();

      toast.success(`Application ${actionType} successfully!`);
      setIsModalVisible(false);
      setRemarks("");
      refetch();
    } catch (error) {
      toast.error(
        error?.data?.message || `Failed to ${actionType} application`
      );
    }
  };

  const openModal = (type) => {
    setActionType(type);
    setIsModalVisible(true);
  };

  return (
    <div className="p-6">
      <ToastContainer />
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        {/* Header */}
        <div className="mb-6">
          <Button
            type="text"
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate("/consultant-applications")}
            className="mb-4"
          >
            Back to Applications
          </Button>

          <div className="flex items-center justify-between">
            <div>
              <Title level={2} style={{ margin: 0, color: "#1a1a1a" }}>
                Application Details
              </Title>
              <Text type="secondary" className="text-base">
                Review consultant application and take action
              </Text>
            </div>

            <Tag
              color={getStatusColor(application.status)}
              icon={getStatusIcon(application.status)}
              className="capitalize text-lg px-4 py-2"
            >
              {application.status}
            </Tag>
          </div>
        </div>

        <Row gutter={[24, 24]}>
          {/* Left Column - Personal Information */}
          <Col xs={24} lg={16}>
            <Card
              title={
                <div className="flex items-center">
                  <UserOutlined
                    className="mr-2"
                    style={{ color: primaryColor }}
                  />
                  Personal Information
                </div>
              }
              className="mb-6"
            >
              <Row gutter={[24, 24]}>
                <Col xs={24} sm={8}>
                  <div className="text-center">
                    {application.image ? (
                      <Image
                        src={application.image}
                        alt="Profile"
                        width={120}
                        height={120}
                        className="rounded-full object-cover"
                        style={{ border: `3px solid ${primaryColor}20` }}
                      />
                    ) : (
                      <Avatar
                        size={120}
                        icon={<UserOutlined />}
                        style={{
                          backgroundColor: `${primaryColor}20`,
                          color: primaryColor,
                        }}
                      />
                    )}
                  </div>
                </Col>

                <Col xs={24} sm={16}>
                  <Descriptions column={1} size="middle">
                    <Descriptions.Item label="Full Name">
                      <Text strong className="text-lg">
                        {application.firstName} {application.lastName}
                      </Text>
                    </Descriptions.Item>
                    <Descriptions.Item label="Categories">
                      <div>
                        {Array.isArray(application.category) ? (
                          application.category.map((cat, index) => (
                            <Tag
                              key={index}
                              color={primaryColor}
                              className="mb-1"
                            >
                              {cat}
                            </Tag>
                          ))
                        ) : (
                          <Tag color={primaryColor}>{application.category}</Tag>
                        )}
                      </div>
                    </Descriptions.Item>
                    <Descriptions.Item label="Email">
                      <div className="flex items-center">
                        <MailOutlined className="mr-2" />
                        {application?.user?.email}
                      </div>
                    </Descriptions.Item>
                    <Descriptions.Item label="Phone">
                      <div className="flex items-center">
                        <PhoneOutlined className="mr-2" />
                        {application.phone}
                      </div>
                    </Descriptions.Item>
                  </Descriptions>
                </Col>
              </Row>
            </Card>

            {/* About Section */}
            <Card title="About" className="mb-6">
              <Paragraph className="text-base leading-relaxed">
                {application.about}
              </Paragraph>
            </Card>

            {/* Service Details */}
            <Card title="Service Details" className="mb-6">
              <Row gutter={[24, 24]}>
                <Col xs={24} sm={12}>
                  <div className="text-center p-4 bg-gray-50 rounded-lg">
                    <DollarOutlined
                      style={{
                        fontSize: "24px",
                        color: primaryColor,
                        marginBottom: "8px",
                      }}
                    />
                    <div>
                      <Text type="secondary" className="block">
                        Price per Minute
                      </Text>
                      <Text
                        strong
                        className="text-xl"
                        style={{ color: primaryColor }}
                      >
                        ₹{application.pricePerMinute}
                      </Text>
                    </div>
                  </div>
                </Col>

                <Col xs={24} sm={12}>
                  <div className="text-center p-4 bg-gray-50 rounded-lg">
                    <CommentOutlined
                      style={{
                        fontSize: "24px",
                        color: primaryColor,
                        marginBottom: "8px",
                      }}
                    />
                    <div>
                      <Text type="secondary" className="block">
                        Communication Types
                      </Text>
                      <div className="mt-2">
                        {application.communicationType?.map((type) => (
                          <Tag key={type} className="capitalize">
                            {type}
                          </Tag>
                        ))}
                      </div>
                    </div>
                  </div>
                </Col>
              </Row>

              <Divider />
            </Card>
          </Col>

          {/* Right Column - Actions & Timeline */}
          <Col xs={24} lg={8}>
            {/* Actions Card */}
            {application.status === "pending" && (
              <Card title="Actions" className="mb-6">
                <Space direction="vertical" style={{ width: "100%" }}>
                  <Button
                    type="primary"
                    icon={<CheckCircleOutlined />}
                    onClick={() => openModal("approved")}
                    block
                    size="large"
                    style={{
                      backgroundColor: "#52c41a",
                      borderColor: "#52c41a",
                    }}
                  >
                    Approve Application
                  </Button>

                  <Button
                    danger
                    icon={<CloseCircleOutlined />}
                    onClick={() => openModal("rejected")}
                    block
                    size="large"
                  >
                    Reject Application
                  </Button>
                </Space>
              </Card>
            )}

            {/* Application Info */}
            <Card title="Application Info" className="mb-6">
              <Descriptions column={1} size="small">
                <Descriptions.Item label="Application ID">
                  <Text code>{application._id}</Text>
                </Descriptions.Item>
                <Descriptions.Item label="Submitted On">
                  <div className="flex items-center">
                    <CalendarOutlined className="mr-2" />
                    {new Date(application.createdAt).toLocaleDateString(
                      "en-IN",
                      {
                        year: "numeric",
                        month: "long",
                        day: "numeric",
                        hour: "2-digit",
                        minute: "2-digit",
                      }
                    )}
                  </div>
                </Descriptions.Item>
                {application.updatedAt !== application.createdAt && (
                  <Descriptions.Item label="Last Updated">
                    <div className="flex items-center">
                      <CalendarOutlined className="mr-2" />
                      {new Date(application.updatedAt).toLocaleDateString(
                        "en-IN",
                        {
                          year: "numeric",
                          month: "long",
                          day: "numeric",
                          hour: "2-digit",
                          minute: "2-digit",
                        }
                      )}
                    </div>
                  </Descriptions.Item>
                )}
              </Descriptions>
            </Card>

            {/* Remarks */}
            {application.remarks && (
              <Card title="Remarks" className="mb-6">
                <Paragraph className="text-base">
                  {application.remarks}
                </Paragraph>
              </Card>
            )}
          </Col>
        </Row>
      </motion.div>

      {/* Action Modal */}
      <Modal
        title={`${
          actionType === "approved" ? "Approve" : "Reject"
        } Application`}
        visible={isModalVisible}
        onOk={handleStatusUpdate}
        onCancel={() => {
          setIsModalVisible(false);
          setRemarks("");
        }}
        confirmLoading={isUpdating}
        okText={actionType === "approved" ? "Approve" : "Reject"}
        okButtonProps={{
          danger: actionType === "rejected",
          style:
            actionType === "approved"
              ? { backgroundColor: "#52c41a", borderColor: "#52c41a" }
              : {},
        }}
      >
        <div className="py-4">
          <Text className="block mb-4">
            Are you sure you want to{" "}
            {actionType === "approved" ? "approve" : "reject"} this application?
          </Text>

          <TextArea
            placeholder={
              actionType === "approved"
                ? "Add any notes for the applicant (optional)"
                : "Please provide reason for rejection (required)"
            }
            value={remarks}
            onChange={(e) => setRemarks(e.target.value)}
            rows={4}
            required={actionType === "rejected"}
          />
        </div>
      </Modal>
    </div>
  );
};
